import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {AppState} from '../../state/reducers';
import {Router} from '@angular/router';
import * as AutomationActions from './actions';
import { catchError, filter, map, mergeMap, of, withLatestFrom } from 'rxjs';
import { AutomationFlowConfig } from '../../interfaces/automation-flow-config.interface';
import { Noop } from '../../state/actions';
import { AutomationService } from '../automation.service';
import { getCurrentPractice } from '../../practices/state/selectors';
import { AutomationRule } from '../../interfaces/automation-rule.interface';
import { MessageService } from 'primeng/api';

@Injectable()
export class AutomationEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private router: Router,
    public messageService: MessageService,
    private automationService: AutomationService
  ) {}

  getFlowConfigurations$ = createEffect(() => this.actions$.pipe(
    ofType(AutomationActions.GetFlowConfigurations),
    mergeMap((action) => {
      return this.automationService.getFlowConfigurations()
        .pipe(
          map((result: AutomationFlowConfig[]) => {
            return AutomationActions.GetFlowConfigurationsSuccess({configs: result});
          }),
          catchError(() => {
            // this.messageService.add({
            //   severity: 'error',
            //   summary: 'Error',
            //   detail: 'An error occurred when getting templates',
            //   life: 5000
            // });
            return of(Noop());
          })
        );
    })
  ));

  getFlowRules$ = createEffect(() => this.actions$.pipe(
    ofType(AutomationActions.GetFlowRules),
    withLatestFrom(this.store.select(getCurrentPractice)),
    filter(([action, practice]) => practice !== null),
    mergeMap(([action, practice]) => this.automationService.getRules(action.flow, practice!.id)
      .pipe(
        map((result: AutomationRule[]) => {
          return AutomationActions.GetFlowRulesSuccess({rules: result});
        }),
        catchError((error) => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              "An error occured when trying to fetch the flows for this clinic",
            life: 10000,
          });
          return of(AutomationActions.GetFlowRulesFail());
        })
      ))
    )
  );

  deleteRule$ = createEffect(() => this.actions$.pipe(
    ofType(AutomationActions.DeleteRule),
    withLatestFrom(this.store.select(getCurrentPractice)),
    filter(([action, practice]) => practice !== null),
    mergeMap(([action, practice]) => this.automationService.deleteRule(action.id, practice!.id)
      .pipe(
        map((result: boolean) => {
          return AutomationActions.DeleteRuleSuccess({id: action.id});
        }),
        catchError((error) => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              "An error occured when trying to delete this rule",
            life: 10000,
          });
          return of(AutomationActions.DeleteRuleFail());
        })
      ))
    )
  );

  setDryRunStatus$ = createEffect(() => this.actions$.pipe(
    ofType(AutomationActions.SetDryRunStatus),
    withLatestFrom(this.store.select(getCurrentPractice)),
    filter(([action, practice]) => practice !== null),
    mergeMap(([action, practice]) => this.automationService.setDryRunStatus(action.id, action.dryRun, practice!.id)
      .pipe(
        map((result: boolean) => {
          return AutomationActions.SetDryRunStatusSuccess({id: action.id, dryRun: action.dryRun});
        }),
        catchError((error) => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              "An error occured when trying to update this rule",
            life: 10000,
          });
          return of(AutomationActions.SetDryRunStatusFail());
        })
      ))
    )
  );

  saveRule$ = createEffect(() => this.actions$.pipe(
    ofType(AutomationActions.SaveRule),
    withLatestFrom(this.store.select(getCurrentPractice)),
    filter(([action, practice]) => practice !== null),
    mergeMap(([action, practice]) => this.automationService.saveRule(action.body, practice!.id)
      .pipe(
        map((result: AutomationRule) => {
          return AutomationActions.SaveRuleSuccess({rule: result});
        }),
        catchError((error) => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              "An error occured when trying to save this rule",
            life: 10000,
          });
          return of(AutomationActions.SaveRuleFail());
        })
      ))
    )
  );
}
